import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus, MoreHorizontal, Eye, Trash2, AlertCircle, CreditCard, Minus, X, ArrowDownUp,
  CalendarCheck, RotateCcw, Calendar as CalendarIcon, BarChart3, Pencil, Search, Ban, PackageCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import { PAYMENT_MODES, formatMode } from "@/lib/paymentModes";
import { usePagination } from "@/components/Pagination";

const TABS = [
  { id: "tuition", label: "Tuitions" },
  { id: "modular_class", label: "Modular Class" },
  { id: "individual_session", label: "Individual Sessions" },
  { id: "uniforms", label: "Uniforms" },
  { id: "books", label: "Books" },
  { id: "events", label: "Events" },
  { id: "others", label: "Others" },
];
const INVENTORY_CATS = { uniforms: "uniforms", books: "books", others: "others" };

function todayISO() { return new Date().toISOString().slice(0, 10); }

function fmtRowDate(d) {
  if (!d) return "—";
  // Accept both "YYYY-MM-DD" and full ISO datetime — show date in PH-friendly format.
  const iso = String(d).slice(0, 10);
  const [y, m, day] = iso.split("-").map(Number);
  if (!y || !m || !day) return iso || "—";
  const dt = new Date(y, m - 1, day);
  return dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
}

export default function StudentAccountsPage() {
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  const currency = user?.currency_symbol || "₱";
  const perms = user?.permissions || {};
  const isOwnerLike = user?.role === "school_owner" || user?.role === "sub_owner";
  const canForfeit = isOwnerLike || !!perms.session_forfeit;
  const [tab, setTab] = useState("tuition");
  const [records, setRecords] = useState([]);
  const [students, setStudents] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [attendOpen, setAttendOpen] = useState(null); // record to mark attended
  const [unmarkConfirm, setUnmarkConfirm] = useState(null);
  const [forfeitConfirm, setForfeitConfirm] = useState(null);
  const [releasing, setReleasing] = useState(null);

  const branchId = selectedBranch?.id;
  const fmt = (v) => `${currency}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const load = useCallback(async () => {
    if (!branchId) { setRecords([]); setStudents([]); setInventoryItems([]); setEvents([]); return; }
    setLoading(true);
    try {
      const [{ data: r }, { data: s }, { data: inv }, { data: ev }] = await Promise.all([
        api.get("/student-accounts", { params: { branch_id: branchId } }),
        api.get("/students", { params: { branch_id: branchId } }),
        api.get("/inventory-items", { params: { branch_id: branchId } }),
        api.get("/events", { params: { branch_id: branchId } }),
      ]);
      setRecords(r || []); setStudents(s || []);
      setInventoryItems(inv || []); setEvents(ev || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const [sortDir, setSortDir] = useState("desc"); // 'desc' | 'asc'
  const [sortField, setSortField] = useState("date"); // 'date' | 'student'
  // Attendance filter — only meaningful on the Individual Sessions tab.
  const [attendFilter, setAttendFilter] = useState("all");
  // Cross-tab text search (student name + description) + date range filters.
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Class / Section filters — applied to the parent student's enrollment.
  const [classFilter, setClassFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");

  // Build a quick student-id → { name, class_id, section_id } index.
  const studentIndex = useMemo(() => {
    const m = {};
    for (const s of students) {
      const fn = [s.name, (s.middle_initial || "").trim(), s.last_name]
        .filter(Boolean).join(" ").trim() || s.full_name || "";
      m[s.id] = { name: fn.toLowerCase(), class_id: s.class_id || "", section_id: s.section_id || "" };
    }
    return m;
  }, [students]);
  const studentNameById = useMemo(() => {
    const m = {};
    for (const id of Object.keys(studentIndex)) m[id] = studentIndex[id].name;
    return m;
  }, [studentIndex]);

  // Distinct classes / sections derived from the loaded students.
  const classOptions = useMemo(() => {
    const seen = new Map();
    for (const s of students) {
      if (s.class_id && !seen.has(s.class_id)) seen.set(s.class_id, s.class_name || s.class_id);
    }
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [students]);
  const sectionOptions = useMemo(() => {
    const seen = new Map();
    for (const s of students) {
      if (s.section_id && (!classFilter || s.class_id === classFilter) && !seen.has(s.section_id)) {
        seen.set(s.section_id, s.section_name || s.section_id);
      }
    }
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [students, classFilter]);

  const filtered = useMemo(() => {
    let rows = records.filter((r) => r.category === tab);
    if (tab === "individual_session" && attendFilter !== "all") {
      rows = rows.filter((r) => {
        if (attendFilter === "unattended") return !r.attended && !r.forfeited;
        if (attendFilter === "attended") return r.attended && !r.is_makeup;
        if (attendFilter === "makeup") return r.attended && r.is_makeup;
        if (attendFilter === "unpaid_attended") return r.attended && r.status !== "paid";
        if (attendFilter === "forfeited") return !!r.forfeited;
        return true;
      });
    }
    // Date range filter — uses the row's `date` (fallback created_at).
    if (dateFrom) {
      rows = rows.filter((r) => (String(r.date || r.created_at || "").slice(0, 10)) >= dateFrom);
    }
    if (dateTo) {
      rows = rows.filter((r) => (String(r.date || r.created_at || "").slice(0, 10)) <= dateTo);
    }
    // Class / Section filters — resolved via the parent student's enrollment.
    if (classFilter) {
      rows = rows.filter((r) => (studentIndex[r.student_id]?.class_id || "") === classFilter);
    }
    if (sectionFilter) {
      rows = rows.filter((r) => (studentIndex[r.student_id]?.section_id || "") === sectionFilter);
    }
    // Text search — student name + description (case-insensitive).
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => {
        const name = studentNameById[r.student_id] || "";
        const desc = String(r.description || "").toLowerCase();
        return name.includes(q) || desc.includes(q);
      });
    }
    return [...rows].sort((a, b) => {
      if (sortField === "student") {
        const an = studentNameById[a.student_id] || "";
        const bn = studentNameById[b.student_id] || "";
        if (an === bn) return 0;
        const cmp = an.localeCompare(bn, undefined, { sensitivity: "base" });
        return sortDir === "asc" ? cmp : -cmp;
      }
      const ad = a.date || a.created_at || "";
      const bd = b.date || b.created_at || "";
      if (ad === bd) return 0;
      return sortDir === "asc" ? (ad < bd ? -1 : 1) : (ad < bd ? 1 : -1);
    });
  }, [records, tab, sortDir, sortField, attendFilter, dateFrom, dateTo, searchQuery, classFilter, sectionFilter, studentIndex, studentNameById]);

  // Pre-compute counts for the filter chips (only Individual Sessions tab).
  const attendCounts = useMemo(() => {
    const rows = records.filter((r) => r.category === "individual_session");
    return {
      all: rows.length,
      unattended: rows.filter((r) => !r.attended && !r.forfeited).length,
      attended: rows.filter((r) => r.attended && !r.is_makeup).length,
      makeup: rows.filter((r) => r.attended && r.is_makeup).length,
      unpaid_attended: rows.filter((r) => r.attended && r.status !== "paid").length,
      forfeited: rows.filter((r) => r.forfeited).length,
    };
  }, [records]);

  // Pagination — resets when tab, sort, branch or attendance filter changes.
  const { pageItems: pagedRows, controls: paginationControls } = usePagination(
    filtered,
    {
      pageSize: 25,
      label: "records",
      resetKey: `${tab}|${sortDir}|${branchId}|${attendFilter}|${dateFrom}|${dateTo}|${searchQuery}`,
    },
  );

  const addBtnLabel = useMemo(() => {
    const map = {
      tuition: "Add Tuition Record",
      modular_class: "Add Modular Class Record",
      individual_session: "Add Session Record",
      uniforms: "Add Uniform Record",
      books: "Add Book Record",
      events: "Add Event Record",
      others: "Add Record",
    };
    return map[tab] || "Add Record";
  }, [tab]);

  if (!selectedBranch) {
    return (
      <>
        <Header />
        <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center max-w-xl" data-testid="no-branch-picked">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#FFF3E0] flex items-center justify-center mb-3">
            <AlertCircle className="w-7 h-7 text-[#E65100]" />
          </div>
          <p className="font-heading text-lg font-semibold text-foreground">Pick a branch first</p>
          <p className="text-brand-inkMuted mt-1 text-sm">Student accounts are scoped per branch.</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="inline-flex p-1 rounded-2xl bg-brand-purpleLight/50 border border-brand-purple/10 overflow-x-auto" role="tablist" data-testid="sa-tabs">
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id} type="button" role="tab"
                onClick={() => setTab(t.id)}
                data-testid={`sa-tab-${t.id}`}
                className={`px-4 h-9 rounded-xl text-sm font-heading font-semibold whitespace-nowrap transition ${
                  active ? "bg-white shadow-sm text-brand-purple" : "text-brand-inkMuted hover:text-foreground"
                }`}
              >{t.label}</button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
            variant="outline"
            className="rounded-2xl h-11 px-3 border-brand-purple/20"
            title={sortDir === "desc" ? "Sort: Newest first (click for oldest first)" : "Sort: Oldest first (click for newest first)"}
            data-testid="sa-sort-toggle"
          >
            <ArrowDownUp className="w-4 h-4 mr-1.5" />
            <span className="hidden sm:inline">{sortDir === "desc" ? "Newest first" : "Oldest first"}</span>
          </Button>
          <Button onClick={() => setFormOpen(true)} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white shadow-sm h-11 px-5" data-testid="sa-add-btn">
            <Plus className="w-4 h-4 mr-1.5" /> {addBtnLabel}
          </Button>
        </div>
      </div>

      {/* Search + date-range + class/section filter row — applies across all tabs. */}
      <section className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm p-3 sm:p-4 mb-4" data-testid="sa-filter-bar">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_repeat(4,minmax(140px,1fr))_auto] items-end">
          <div>
            <label className="text-[11px] font-heading font-bold uppercase tracking-wider text-brand-inkMuted mb-1.5 block">Search</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-inkMuted pointer-events-none" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by student name or description…"
                className="pl-9 rounded-2xl border-brand-purple/20 h-11"
                data-testid="sa-search"
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-heading font-bold uppercase tracking-wider text-brand-inkMuted mb-1.5 block">Class</label>
            <select
              value={classFilter}
              onChange={(e) => { setClassFilter(e.target.value); setSectionFilter(""); }}
              className="w-full h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 text-sm"
              data-testid="sa-class-filter"
            >
              <option value="">All classes</option>
              {classOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-heading font-bold uppercase tracking-wider text-brand-inkMuted mb-1.5 block">Section</label>
            <select
              value={sectionFilter}
              onChange={(e) => setSectionFilter(e.target.value)}
              className="w-full h-11 rounded-2xl border border-brand-purple/20 bg-white px-3 text-sm disabled:opacity-50"
              disabled={sectionOptions.length === 0}
              data-testid="sa-section-filter"
            >
              <option value="">All sections</option>
              {sectionOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-heading font-bold uppercase tracking-wider text-brand-inkMuted mb-1.5 block">From</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="rounded-2xl border-brand-purple/20 h-11"
              data-testid="sa-date-from"
            />
          </div>
          <div>
            <label className="text-[11px] font-heading font-bold uppercase tracking-wider text-brand-inkMuted mb-1.5 block">To</label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="rounded-2xl border-brand-purple/20 h-11"
              data-testid="sa-date-to"
            />
          </div>
          {(searchQuery || dateFrom || dateTo || classFilter || sectionFilter) && (
            <Button
              type="button"
              variant="outline"
              onClick={() => { setSearchQuery(""); setDateFrom(""); setDateTo(""); setClassFilter(""); setSectionFilter(""); }}
              className="rounded-2xl h-11 px-3 border-brand-purple/20"
              data-testid="sa-clear-filters"
            >
              <X className="w-4 h-4 mr-1.5" /> Clear
            </Button>
          )}
        </div>
      </section>

      {tab === "individual_session" && (
        <AttendanceFilterChips
          value={attendFilter}
          onChange={setAttendFilter}
          counts={attendCounts}
        />
      )}

      <div className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-hidden" data-testid="sa-list">
        {loading ? <p className="p-6 text-brand-inkMuted">Loading...</p>
        : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-purpleLight flex items-center justify-center mb-3">
              <CreditCard className="w-7 h-7 text-brand-purple" />
            </div>
            <p className="font-heading text-lg font-semibold text-foreground">No records yet</p>
            <p className="text-brand-inkMuted mt-1 text-sm">Click {addBtnLabel} to add one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F6F8FE] text-brand-inkMuted">
                <tr>
                  <th
                    onClick={() => {
                      if (sortField === "date") setSortDir((d) => (d === "desc" ? "asc" : "desc"));
                      else { setSortField("date"); setSortDir("desc"); }
                    }}
                    className="text-left px-3 py-2.5 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-brand-purple"
                    data-testid="sa-th-date"
                    title="Click to sort by date"
                  >
                    DATE {sortField === "date" && <span className="ml-1 text-brand-purple">{sortDir === "desc" ? "▼" : "▲"}</span>}
                  </th>
                  <th
                    onClick={() => {
                      if (sortField === "student") setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                      else { setSortField("student"); setSortDir("asc"); }
                    }}
                    className="text-left px-3 py-2.5 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-brand-purple"
                    data-testid="sa-th-student"
                    title="Click to sort by student name"
                  >
                    STUDENT {sortField === "student" && <span className="ml-1 text-brand-purple">{sortDir === "asc" ? "▲" : "▼"}</span>}
                  </th>
                  <th className="text-left px-3 py-2.5 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">DESCRIPTION</th>
                  <th className="text-right px-3 py-2.5 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">AMOUNT</th>
                  <th className="text-right px-3 py-2.5 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">PAID</th>
                  <th className="text-right px-3 py-2.5 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">BALANCE</th>
                  <th className="text-center px-3 py-2.5 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">STATUS</th>
                  <th className="text-center px-3 py-2.5 font-heading font-semibold text-[11px] tracking-wider whitespace-nowrap">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((r, i) => (
                  <tr key={r.id} className={i % 2 ? "bg-white" : "bg-[#FAFBFE]"} data-testid={`sa-row-${r.id}`}>
                    <td className="px-3 py-2.5 text-foreground whitespace-nowrap tabular-nums">{fmtRowDate(r.date || r.created_at)}</td>
                    <td className="px-3 py-2.5 font-heading font-semibold text-foreground whitespace-nowrap uppercase">{r.student_name || "—"}</td>
                    <td className="px-3 py-2.5 text-foreground whitespace-nowrap max-w-[260px] truncate" title={r.description || (r.inventory_item_name || r.event_name || "—")}>{r.description || (r.inventory_item_name || r.event_name || "—")}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{fmt(r.amount)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{fmt(r.paid_amount)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap font-heading font-semibold">{fmt(r.balance)}</td>
                    <td className="px-3 py-2.5 text-center whitespace-nowrap">
                      {r.category === "individual_session"
                        ? <SessionStatusBadge record={r} />
                        : <StatusPill status={r.status} />}
                    </td>
                    <td className="px-3 py-2.5 text-center whitespace-nowrap">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="rounded-full h-8 w-8" data-testid={`sa-more-${r.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-xl min-w-[180px]">
                          <DropdownMenuItem onClick={() => setViewing(r)} data-testid={`sa-view-${r.id}`}>
                            <Eye className="w-4 h-4 mr-2" /> View
                          </DropdownMenuItem>
                          {r.status !== "paid" && (
                            <DropdownMenuItem onClick={() => setPayOpen(r)} className="text-brand-purple" data-testid={`sa-pay-${r.id}`}>
                              <CreditCard className="w-4 h-4 mr-2" /> Pay
                            </DropdownMenuItem>
                          )}
                          {r.category === "individual_session" && !r.attended && (
                            <DropdownMenuItem onClick={() => setAttendOpen(r)} className="text-[#2E7D32]" data-testid={`sa-attend-${r.id}`}>
                              <CalendarCheck className="w-4 h-4 mr-2" /> Mark Attended
                            </DropdownMenuItem>
                          )}
                          {r.category === "individual_session" && r.attended && (
                            <DropdownMenuItem onClick={() => setUnmarkConfirm(r)} data-testid={`sa-unattend-${r.id}`}>
                              <RotateCcw className="w-4 h-4 mr-2" /> Unmark Attended
                            </DropdownMenuItem>
                          )}
                          {r.category === "individual_session" && r.status === "paid" && !r.attended && !r.forfeited && canForfeit && (
                            <DropdownMenuItem onClick={() => setForfeitConfirm(r)} className="text-[#8D6E00]" data-testid={`sa-forfeit-${r.id}`}>
                              <Ban className="w-4 h-4 mr-2" /> Forfeit
                            </DropdownMenuItem>
                          )}
                          {r.category === "individual_session" && r.forfeited && canForfeit && (
                            <DropdownMenuItem onClick={() => setForfeitConfirm({ ...r, __unforfeit: true })} data-testid={`sa-unforfeit-${r.id}`}>
                              <RotateCcw className="w-4 h-4 mr-2" /> Undo Forfeit
                            </DropdownMenuItem>
                          )}
                          {["uniforms", "books", "events", "others"].includes(r.category) && (
                            (() => {
                              const released = (r.releases || []).reduce((s, x) => s + (Number(x.quantity) || 0), 0);
                              const remaining = Math.max(0, Number(r.quantity || 0) - released);
                              const fullyPaid = Number(r.paid_amount || 0) + 0.001 >= Number(r.amount || 0);
                              if (remaining <= 0) return null;
                              return (
                                <DropdownMenuItem
                                  onClick={() => {
                                    if (!fullyPaid) { toast.error("Cannot release — item must be fully paid first"); return; }
                                    setReleasing(r);
                                  }}
                                  className={fullyPaid ? "text-[#166534]" : "text-brand-inkMuted opacity-60"}
                                  data-testid={`sa-release-${r.id}`}
                                >
                                  <PackageCheck className="w-4 h-4 mr-2" /> Release
                                </DropdownMenuItem>
                              );
                            })()
                          )}
                          <DropdownMenuItem onClick={() => setDeleting(r)} className="text-[#C62828]" data-testid={`sa-delete-${r.id}`}>
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-3 pb-2">
              {paginationControls}
            </div>
          </div>
        )}
      </div>

      {formOpen && (
        <AddRecordDialog
          open={formOpen} onClose={() => setFormOpen(false)}
          branchId={branchId} category={tab}
          students={students} inventoryItems={inventoryItems} events={events}
          currency={currency}
          currentUserName={user?.name || user?.email || ""}
          onSaved={() => { setFormOpen(false); load(); }}
        />
      )}
      {payOpen && (
        <PayDialog
          record={payOpen} currency={currency}
          onClose={() => setPayOpen(null)}
          onSaved={() => { setPayOpen(null); load(); }}
        />
      )}
      {viewing && (
        <ViewDialog record={viewing} currency={currency} onClose={() => setViewing(null)} onChanged={load} />
      )}

      {attendOpen && (
        <MarkAttendedDialog
          record={attendOpen}
          onClose={() => setAttendOpen(null)}
          onSaved={() => { setAttendOpen(null); load(); }}
        />
      )}

      {releasing && (
        <ReleaseDialog
          record={releasing}
          currentUserName={user?.name || user?.email || ""}
          onClose={() => setReleasing(null)}
          onSaved={() => { setReleasing(null); load(); }}
        />
      )}

      <AlertDialog open={!!unmarkConfirm} onOpenChange={(v) => !v && setUnmarkConfirm(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Unmark attendance?</AlertDialogTitle>
            <AlertDialogDescription className="text-brand-inkMuted">
              The session will revert to <strong>Unattended</strong>. The student can be marked attended again later — e.g. when they take the make-up class.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await api.post(`/student-accounts/${unmarkConfirm.id}/unmark-attended`);
                  toast.success("Attendance reverted — you can re-mark when the make-up happens");
                  setUnmarkConfirm(null); load();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="rounded-2xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              data-testid="sa-confirm-unattend"
            >Unmark</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!forfeitConfirm} onOpenChange={(v) => !v && setForfeitConfirm(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              {forfeitConfirm?.__unforfeit ? "Undo forfeit?" : "Forfeit this session?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-brand-inkMuted">
              {forfeitConfirm?.__unforfeit
                ? "The session will become redeemable again."
                : "The session was already paid but will no longer be redeemable. Payment stays as income; the status becomes 'Paid Forfeited'."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  const endpoint = forfeitConfirm.__unforfeit ? "unforfeit" : "forfeit";
                  await api.post(`/student-accounts/${forfeitConfirm.id}/${endpoint}`);
                  toast.success(forfeitConfirm.__unforfeit ? "Forfeit reversed" : "Session marked Paid Forfeited");
                  setForfeitConfirm(null); load();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className={`rounded-2xl text-white ${forfeitConfirm?.__unforfeit ? "bg-[#2563EB] hover:bg-[#1D4ED8]" : "bg-[#8D6E00] hover:bg-[#725800]"}`}
              data-testid="sa-confirm-forfeit"
            >{forfeitConfirm?.__unforfeit ? "Undo Forfeit" : "Forfeit"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete this record?</AlertDialogTitle>
            <AlertDialogDescription className="text-brand-inkMuted">
              This record will be removed. If it deducted inventory, that quantity will be restored.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-2xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                try {
                  await api.delete(`/student-accounts/${deleting.id}`);
                  toast.success("Record deleted");
                  setDeleting(null); load();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="rounded-2xl bg-[#C62828] hover:bg-[#B71C1C] text-white"
              data-testid="sa-confirm-delete"
            >Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function Header() {
  return (
    <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground tracking-tight">Student Accounts</h1>
        <p className="text-brand-inkMuted mt-0.5 text-sm">Track tuition, sessions, uniforms, books, events and other student charges.</p>
      </div>
      <Link
        to="/students/accounts/summary"
        data-testid="sa-open-summary-btn"
        className="inline-flex items-center gap-1.5 rounded-2xl border border-brand-purple/30 bg-white text-brand-purple hover:bg-brand-purpleLight hover:border-brand-purple px-4 h-11 font-heading font-semibold text-sm transition shadow-sm"
      >
        <BarChart3 className="w-4 h-4" /> View Account Summary
      </Link>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    paid: ["bg-[#E8F5E9]", "text-[#2E7D32]", "Paid"],
    partial: ["bg-[#FFF3E0]", "text-[#E65100]", "Partial"],
    unpaid: ["bg-[#FDECEA]", "text-[#C62828]", "Unpaid"],
  };
  const [bg, fg, label] = map[status] || map.unpaid;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-heading font-semibold ${bg} ${fg}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" /> {label}
    </span>
  );
}

/* ---------------- Individual-session attendance filter chips ---------------- */
function AttendanceFilterChips({ value, onChange, counts }) {
  // Designed to scan in one glance — each chip shows the count of matching
  // rows so admins know what's worth clicking. "Unpaid + Attended" is the
  // collection-priority view (students who came in but haven't paid yet).
  const CHIPS = [
    { id: "all",             label: "All",               className: "border-slate-300 text-slate-700 data-[on=true]:bg-slate-800 data-[on=true]:text-white data-[on=true]:border-slate-800" },
    { id: "unattended",      label: "Unattended",        className: "border-[#F6E1A6] text-[#8D6E00] data-[on=true]:bg-[#FFF8E1] data-[on=true]:border-[#E0B400]" },
    { id: "attended",        label: "Attended",          className: "border-[#B7E2BB] text-[#2E7D32] data-[on=true]:bg-[#E8F5E9] data-[on=true]:border-[#2E7D32]" },
    { id: "makeup",          label: "Make-ups",          className: "border-[#D7CDF0] text-[#5E35B1] data-[on=true]:bg-[#EDE7F6] data-[on=true]:border-[#5E35B1]" },
    { id: "unpaid_attended", label: "Unpaid + Attended", className: "border-[#F2B8BA] text-[#C62828] data-[on=true]:bg-[#FDECEA] data-[on=true]:border-[#C62828]" },
    { id: "forfeited",       label: "Forfeited",         className: "border-[#E0B400] text-[#8D6E00] data-[on=true]:bg-[#FFF8E1] data-[on=true]:border-[#8D6E00]" },
  ];
  return (
    <div className="mb-4 flex flex-wrap gap-2" data-testid="sa-attend-filter-chips">
      {CHIPS.map((c) => {
        const on = value === c.id;
        const n = counts[c.id] ?? 0;
        return (
          <button
            key={c.id}
            type="button"
            data-on={on}
            data-testid={`sa-attend-filter-${c.id}`}
            onClick={() => onChange(c.id)}
            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-heading font-semibold border-2 transition ${c.className}`}
          >
            {c.label}
            <span className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-bold ${on ? "bg-white/25" : "bg-slate-100 text-slate-600"}`}>{n}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ---------------- Individual-session attendance pill ---------------- */
function AttendancePill({ record }) {
  if (record.attended) {
    const label = record.is_makeup ? "Attended (Make-up)" : "Attended";
    const tooltip = record.attended_date ? `On ${fmtRowDate(record.attended_date)}` : "";
    const cls = record.is_makeup
      ? "bg-[#EDE7F6] text-[#5E35B1]"
      : "bg-[#E8F5E9] text-[#2E7D32]";
    return (
      <span title={tooltip} data-testid={`sa-attended-pill-${record.id}`}
        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-heading font-semibold ${cls}`}>
        <CalendarCheck className="w-3 h-3" /> {label}
      </span>
    );
  }
  return (
    <span data-testid={`sa-unattended-pill-${record.id}`}
      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-heading font-semibold bg-[#FFF8E1] text-[#8D6E00]">
      <CalendarIcon className="w-3 h-3" /> Unattended
    </span>
  );
}

/* ---------------- Consolidated 4-state Session badge ----------------
 * Single pill on each Individual-Session row picking exactly one of:
 *   • Paid           – paid AND attended (the happy path)
 *   • Paid Unattended – paid, student hasn't shown up yet
 *   • Unpaid Unattended – not paid, not yet attended (just scheduled)
 *   • Unpaid          – attended without paying (student owes for this slot)
 * Make-ups get a small purple "Make-up" appendage so the 4-state semantics
 * stay clean while still flagging replacement sessions.
 */
function SessionStatusBadge({ record }) {
  const paid = record.status === "paid";
  const attended = !!record.attended;
  const forfeited = !!record.forfeited;
  // Labels always include both the financial state AND the attendance state
  // so admins can read the row at a glance without inferring from colour.
  const config = forfeited ?
    { label: "Paid Forfeited",       icon: Ban,           cls: "bg-[#FFF8E1] text-[#8D6E00] border-[#E0B400]" }
    : paid && attended ?
    { label: "Paid + Attended",     icon: CalendarCheck, cls: "bg-[#E8F5E9] text-[#2E7D32] border-[#A5D6A7]" }
    : paid && !attended ?
    { label: "Paid Unattended",     icon: CalendarIcon,  cls: "bg-[#E3F2FD] text-[#0D47A1] border-[#90CAF9]" }
    : !paid && !attended ?
    { label: "Unpaid Unattended",   icon: CalendarIcon,  cls: "bg-[#FFF8E1] text-[#8D6E00] border-[#FFD54F]" }
    :
    { label: "Unpaid + Attended",   icon: CalendarCheck, cls: "bg-[#FDECEA] text-[#C62828] border-[#EF9A9A]" };
  const Icon = config.icon;
  const tip = attended && record.attended_date ? `Attended on ${fmtRowDate(record.attended_date)}` : "";
  return (
    <div className="inline-flex flex-col items-center gap-1" title={tip}>
      <span
        data-testid={`sa-session-badge-${record.id}`}
        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-heading font-semibold border ${config.cls}`}
      >
        <Icon className="w-3 h-3" /> {config.label}
      </span>
      {record.is_makeup && attended && (
        <span
          data-testid={`sa-session-makeup-tag-${record.id}`}
          className="inline-flex items-center gap-1 px-2 py-[1px] rounded-full text-[10px] font-heading font-semibold bg-[#EDE7F6] text-[#5E35B1] border border-[#B39DDB]"
        >
          Make-up
        </span>
      )}
    </div>
  );
}

/* ---------------- Mark Attended dialog ---------------- */
function MarkAttendedDialog({ record, onClose, onSaved }) {
  const [date, setDate] = useState(todayISO());
  const [isMakeup, setIsMakeup] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!date) { toast.error("Pick an attended date"); return; }
    setSaving(true);
    try {
      await api.post(`/student-accounts/${record.id}/mark-attended`, {
        attended_date: date,
        is_makeup: isMakeup,
        note: note.trim(),
      });
      toast.success(`Marked attended on ${fmtRowDate(date)}${isMakeup ? " (Make-up)" : ""}`);
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="sa-attend-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <CalendarCheck className="w-5 h-5 text-[#2E7D32]" /> Mark Session Attended
          </DialogTitle>
          <DialogDescription className="text-brand-inkMuted">
            {record.student_name}{record.description ? ` · ${record.description}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-1">
          <div>
            <Label className="font-heading">Date attended</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              max={todayISO()}
              className="rounded-2xl h-11 mt-1.5" data-testid="sa-attend-date" />
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="sa-attend-makeup-label">
            <input
              type="checkbox" checked={isMakeup}
              onChange={(e) => setIsMakeup(e.target.checked)}
              className="w-4 h-4 rounded accent-[#5E35B1]"
              data-testid="sa-attend-makeup"
            />
            <span className="text-sm font-heading font-semibold text-foreground">Make-up class</span>
            <span className="text-xs text-brand-inkMuted">(tag this session as a replacement for a previous absence)</span>
          </label>
          <div>
            <Label className="font-heading">Note <span className="text-brand-inkMuted font-normal">(optional)</span></Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Rescheduled from Aug 12"
              className="rounded-2xl mt-1.5 min-h-[60px]"
              data-testid="sa-attend-note" />
          </div>
        </div>
        <DialogFooter className="mt-3 flex flex-row gap-2 justify-end">
          <Button variant="outline" onClick={onClose} className="rounded-2xl" data-testid="sa-attend-cancel">Cancel</Button>
          <Button
            onClick={save} disabled={saving}
            className="rounded-2xl bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
            data-testid="sa-attend-save"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------------- Searchable student picker ------------------------ */
function studentLabel(s) {
  return s.full_name || `${s.name || ""} ${s.last_name || ""}`.trim() || "Unnamed student";
}

function StudentSearchPicker({ students, value, onChange }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const rootRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const selected = React.useMemo(
    () => (students || []).find((s) => s.id === value) || null,
    [students, value],
  );

  React.useEffect(() => {
    if (open) setQuery("");
  }, [open]);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return undefined;
    function onDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = React.useMemo(() => {
    if (!q) return students || [];
    return (students || []).filter((s) => {
      const hay = [
        s.full_name,
        s.name,
        s.last_name,
        s.class_name,
        s.section_name,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [students, q]);

  function pick(s) {
    onChange(s.id);
    setOpen(false);
  }

  function clear(e) {
    e.stopPropagation();
    onChange("");
  }

  return (
    <div ref={rootRef} className="relative mt-1.5" data-testid="sa-student-picker">
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); setTimeout(() => inputRef.current?.focus(), 20); }}
        className="w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm flex items-center justify-between gap-2 hover:border-brand-purple/40 transition-colors"
        data-testid="sa-student-select"
      >
        {selected ? (
          <span className="truncate text-foreground">
            {studentLabel(selected)}
            {selected.class_name && <span className="text-brand-inkMuted"> — {selected.class_name}</span>}
          </span>
        ) : (
          <span className="text-brand-inkMuted">Select student</span>
        )}
        <span className="flex items-center gap-1 shrink-0">
          {selected && (
            <span
              role="button"
              tabIndex={0}
              onClick={clear}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") clear(e); }}
              className="text-brand-inkMuted hover:text-red-600 p-0.5 rounded"
              aria-label="Clear selected student"
              data-testid="sa-student-clear"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <Search className="w-4 h-4 text-brand-inkMuted" />
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-2xl border border-input bg-white shadow-floating overflow-hidden" data-testid="sa-student-picker-panel">
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="w-4 h-4 text-brand-inkMuted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type student name, class, or section..."
                className="w-full h-10 rounded-xl border border-input bg-white pl-9 pr-3 text-sm focus:outline-none focus:border-brand-purple/60"
                data-testid="sa-student-search-input"
              />
            </div>
          </div>
          <ul className="max-h-64 overflow-y-auto" role="listbox">
            {filtered.length === 0 ? (
              <li className="px-3 py-6 text-center text-sm text-brand-inkMuted" data-testid="sa-student-picker-empty">
                No students match &quot;{query}&quot;
              </li>
            ) : (
              filtered.slice(0, 200).map((s) => {
                const active = s.id === value;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => pick(s)}
                      className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm transition-colors hover:bg-brand-purpleLight/40 ${active ? "bg-brand-purpleLight/60" : ""}`}
                      data-testid={`sa-student-option-${s.id}`}
                      role="option"
                      aria-selected={active}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-foreground">{studentLabel(s)}</div>
                        {(s.class_name || s.section_name) && (
                          <div className="text-xs text-brand-inkMuted truncate">
                            {[s.class_name, s.section_name].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </div>
                      {active && (
                        <span className="text-[10px] font-bold text-brand-purple uppercase tracking-wider shrink-0">Selected</span>
                      )}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
          {filtered.length > 200 && (
            <div className="px-3 py-1.5 text-[11px] text-brand-inkMuted border-t border-slate-100 bg-slate-50">
              Showing first 200 of {filtered.length} — keep typing to narrow down.
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/* ---------------------------- Add Record Dialog --------------------------- */
function AddRecordDialog({ open, onClose, branchId, category, students, inventoryItems, events, currency, currentUserName, onSaved }) {
  const titleMap = {
    tuition: "Add Tuition Record",
    modular_class: "Add Modular Class Record",
    individual_session: "Add Session Record",
    uniforms: "Add Uniform Record",
    books: "Add Book Record",
    events: "Add Event Record",
    others: "Add Record",
  };
  const categoryLabel = TABS.find((t) => t.id === category)?.label || category;
  const needsInventory = ["uniforms", "books", "others"].includes(category);
  const needsEvent = category === "events";

  const [studentId, setStudentId] = useState("");
  const [date, setDate] = useState(todayISO());
  const [inventoryItemId, setInventoryItemId] = useState("");
  const [eventId, setEventId] = useState("");
  const [description, setDescription] = useState("");
  const [size, setSize] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unitPrice, setUnitPrice] = useState("0");
  const [amount, setAmount] = useState("0");
  const [notes, setNotes] = useState("");
  const [paidNow, setPaidNow] = useState("0");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [referenceNo, setReferenceNo] = useState("");
  const [paymentDate, setPaymentDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);

  // Release fields (uniforms/books/events/others only)
  const [releaseQty, setReleaseQty] = useState("");
  const [releaseDate, setReleaseDate] = useState(todayISO());

  // Session-package mode (only relevant when category === "individual_session")
  const [packageMode, setPackageMode] = useState(false);
  const [pkgNumSessions, setPkgNumSessions] = useState("4");
  const [pkgTotalPrice, setPkgTotalPrice] = useState("0");
  const [pkgDiscount, setPkgDiscount] = useState("0");
  const [pkgDiscountRemarks, setPkgDiscountRemarks] = useState("");
  const [pkgDates, setPkgDates] = useState([todayISO(), todayISO(), todayISO(), todayISO()]);

  // Filter inventory items by category
  const invForCategory = useMemo(() => {
    const c = INVENTORY_CATS[category];
    return c ? inventoryItems.filter((i) => i.category === c) : [];
  }, [inventoryItems, category]);

  // Tuition / Modular Class / Individual Sessions = simple amount-only (no qty/unit price)
  const isSimpleAmount = category === "tuition" || category === "modular_class" || category === "individual_session";

  // Auto-fill unit price and description when picking inventory/event
  useEffect(() => {
    if (inventoryItemId) {
      const it = invForCategory.find((i) => i.id === inventoryItemId);
      if (it) {
        if (Number(unitPrice) === 0) setUnitPrice(String(it.unit_price || 0));
        if (!description) setDescription(it.name);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryItemId]);

  useEffect(() => {
    if (eventId) {
      const ev = events.find((e) => e.id === eventId);
      if (ev) {
        if (Number(unitPrice) === 0) setUnitPrice(String(ev.fee_amount || 0));
        if (!description) setDescription(ev.name);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // Auto-compute amount from unit_price × qty unless user typed something different
  const computedAmount = useMemo(() => {
    const q = Number(quantity || 0); const p = Number(unitPrice || 0);
    return Math.max(0, q * p);
  }, [quantity, unitPrice]);

  useEffect(() => { setAmount(String(computedAmount)); /* eslint-disable-next-line */ }, [computedAmount]);

  // Package mode — keep the dates array length in sync with num_sessions.
  const pkgN = Math.max(1, Math.min(100, parseInt(pkgNumSessions, 10) || 0));
  useEffect(() => {
    if (!packageMode) return;
    setPkgDates((prev) => {
      const next = prev.slice(0, pkgN);
      while (next.length < pkgN) next.push(next[next.length - 1] || todayISO());
      return next;
    });
  }, [pkgN, packageMode]);

  const pkgNetTotal = useMemo(() => {
    const t = Number(pkgTotalPrice || 0);
    const d = Number(pkgDiscount || 0);
    return Math.max(0, t - d);
  }, [pkgTotalPrice, pkgDiscount]);

  const pkgPerSession = useMemo(() => {
    return pkgN > 0 ? Math.round((pkgNetTotal / pkgN) * 100) / 100 : 0;
  }, [pkgNetTotal, pkgN]);

  function updatePkgDate(idx, value) {
    setPkgDates((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!studentId) { toast.error("Pick a student"); return; }

    // ---- Package flow ----
    if (packageMode && category === "individual_session") {
      if (!description.trim()) { toast.error("Add a description for the package"); return; }
      if (pkgN < 1) { toast.error("Number of sessions must be at least 1"); return; }
      if (Number(pkgTotalPrice || 0) <= 0) { toast.error("Enter a positive total price"); return; }
      if (Number(pkgDiscount || 0) > Number(pkgTotalPrice || 0)) { toast.error("Discount cannot exceed total price"); return; }
      if (pkgDates.some((d) => !d)) { toast.error("Every session must have a date"); return; }
      const paidAmt = Math.max(0, Number(paidNow || 0));
      if (paidAmt > pkgNetTotal + 0.001) { toast.error("Paid Now cannot exceed net total"); return; }
      setSaving(true);
      try {
        await api.post("/student-accounts/session-package", {
          branch_id: branchId,
          student_id: studentId,
          description: description.trim(),
          num_sessions: pkgN,
          total_price: Number(pkgTotalPrice || 0),
          discount: Number(pkgDiscount || 0),
          discount_remarks: pkgDiscountRemarks.trim(),
          dates: pkgDates.slice(0, pkgN),
          notes: notes.trim(),
          paid_amount: paidAmt,
          payment_method: paymentMethod,
          reference_no: referenceNo.trim(),
          payment_date: paidAmt > 0 ? paymentDate : null,
        });
        toast.success(`Session package created — ${pkgN} sessions`);
        onSaved();
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setSaving(false); }
      return;
    }

    // ---- Single-record flow ----
    if (category !== "tuition" && category !== "modular_class" && category !== "individual_session" && !description.trim() && !inventoryItemId && !eventId) {
      toast.error("Add a description or pick an item"); return;
    }
    const paidAmt = Math.max(0, Number(paidNow || 0));
    const totalAmt = Number(amount || 0);
    if (paidAmt > totalAmt + 0.001) { toast.error("Paid Now cannot exceed total amount"); return; }
    const rq = Math.max(0, Number(releaseQty || 0));
    const canRelease = needsInventory || needsEvent;
    if (canRelease && rq > 0) {
      if (rq > Number(quantity || 0)) { toast.error("Release qty cannot exceed the record's quantity"); return; }
      if (paidAmt + 0.001 < totalAmt) { toast.error("Cannot release — item must be fully paid"); return; }
    }
    const payload = {
      branch_id: branchId, student_id: studentId, category,
      date, description: description.trim(),
      inventory_item_id: needsInventory ? (inventoryItemId || null) : null,
      event_id: needsEvent ? (eventId || null) : null,
      size: size.trim(),
      quantity: isSimpleAmount ? 1 : Math.max(1, Number(quantity || 1)),
      unit_price: isSimpleAmount ? 0 : Number(unitPrice || 0),
      amount: totalAmt,
      paid_amount: paidAmt,
      notes: notes.trim(),
      source: "outside",
      payment_method: paymentMethod,
      reference_no: referenceNo.trim(),
      payment_date: paidAmt > 0 ? paymentDate : null,
      initial_release_quantity: canRelease && rq > 0 ? rq : 0,
      initial_release_date: canRelease && rq > 0 ? releaseDate : "",
    };
    setSaving(true);
    try {
      await api.post("/student-accounts", payload);
      toast.success("Record added");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const fmt = (v) => `${currency}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[92vh] overflow-y-auto" data-testid="sa-add-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{titleMap[category]}</DialogTitle>
          <DialogDescription className="text-brand-inkMuted">Category: {categoryLabel}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="font-heading">Student <span className="text-[#C62828]">*</span></Label>
            <StudentSearchPicker
              students={students}
              value={studentId}
              onChange={setStudentId}
            />
          </div>

          {category === "individual_session" && (
            <div
              className={`rounded-2xl border p-3 transition-colors ${packageMode ? "border-brand-purple/40 bg-brand-purpleLight/25" : "border-slate-200 bg-slate-50"}`}
              data-testid="sa-package-toggle-row"
            >
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={packageMode}
                  onChange={(e) => setPackageMode(e.target.checked)}
                  className="w-4 h-4 accent-brand-purple"
                  data-testid="sa-package-toggle"
                />
                <span className="font-heading font-semibold text-foreground text-sm">Add Session Package</span>
                <span className="text-xs text-brand-inkMuted">
                  Bundle several pre-scheduled sessions in one go (with optional package discount).
                </span>
              </label>
            </div>
          )}

          {packageMode && category === "individual_session" ? (
            /* -------------------- Session package fields -------------------- */
            <div className="space-y-3" data-testid="sa-package-form">
              <div>
                <Label className="font-heading">Package Description <span className="text-[#C62828]">*</span></Label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g. Speech Therapy — 10-Session Package"
                  className="rounded-2xl h-11 mt-1.5"
                  data-testid="sa-package-desc"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="font-heading">Number of Sessions <span className="text-[#C62828]">*</span></Label>
                  <Input
                    type="number" min="1" max="100" step="1"
                    value={pkgNumSessions}
                    onChange={(e) => setPkgNumSessions(e.target.value)}
                    className="rounded-2xl h-11 mt-1.5 tabular-nums"
                    data-testid="sa-package-num-sessions"
                  />
                </div>
                <div>
                  <Label className="font-heading">Total Price ({currency}) <span className="text-[#C62828]">*</span></Label>
                  <Input
                    type="number" min="0" step="0.01"
                    value={pkgTotalPrice}
                    onChange={(e) => setPkgTotalPrice(e.target.value)}
                    className="rounded-2xl h-11 mt-1.5 tabular-nums"
                    data-testid="sa-package-total-price"
                  />
                </div>
                <div>
                  <Label className="font-heading">Discount ({currency})</Label>
                  <Input
                    type="number" min="0" step="0.01"
                    value={pkgDiscount}
                    onChange={(e) => setPkgDiscount(e.target.value)}
                    className="rounded-2xl h-11 mt-1.5 tabular-nums"
                    data-testid="sa-package-discount"
                  />
                </div>
                <div>
                  <Label className="font-heading">Discount Remarks</Label>
                  <Input
                    value={pkgDiscountRemarks}
                    onChange={(e) => setPkgDiscountRemarks(e.target.value)}
                    disabled={Number(pkgDiscount || 0) <= 0}
                    placeholder="e.g. 10% loyalty · sibling discount"
                    className="rounded-2xl h-11 mt-1.5"
                    data-testid="sa-package-discount-remarks"
                  />
                </div>
              </div>

              {/* Live summary */}
              <div className="grid grid-cols-3 gap-2 text-center bg-white rounded-2xl border border-brand-purple/20 p-3" data-testid="sa-package-summary">
                <div>
                  <div className="text-[10px] text-brand-inkMuted uppercase tracking-wider">Net Total</div>
                  <div className="font-heading text-lg font-bold text-foreground tabular-nums">{currency}{pkgNetTotal.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
                </div>
                <div>
                  <div className="text-[10px] text-brand-inkMuted uppercase tracking-wider">Sessions</div>
                  <div className="font-heading text-lg font-bold text-brand-purple tabular-nums">{pkgN}</div>
                </div>
                <div>
                  <div className="text-[10px] text-brand-inkMuted uppercase tracking-wider">Per Session</div>
                  <div className="font-heading text-lg font-bold text-emerald-700 tabular-nums">{currency}{pkgPerSession.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</div>
                </div>
              </div>

              {/* Per-session date inputs */}
              <div>
                <Label className="font-heading">Session Dates</Label>
                <p className="text-[11px] text-brand-inkMuted mb-2">Pick a date for each of the {pkgN} session(s).</p>
                <div className="grid sm:grid-cols-2 gap-2" data-testid="sa-package-dates">
                  {Array.from({ length: pkgN }).map((_, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="w-16 shrink-0 text-xs font-heading font-semibold text-brand-inkMuted uppercase tracking-wider">
                        Session {idx + 1}
                      </span>
                      <Input
                        type="date"
                        value={pkgDates[idx] || todayISO()}
                        onChange={(e) => updatePkgDate(idx, e.target.value)}
                        className="rounded-2xl h-10 flex-1 text-sm"
                        data-testid={`sa-package-date-${idx}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* -------------------- Single-record fields (unchanged) --------- */
            null
          )}

          {(!packageMode || category !== "individual_session") && isSimpleAmount && (
            <div data-testid="sa-desc-wrap-top">
              <Label className="font-heading">Description <span className="text-[#C62828]">*</span></Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={category === "tuition" ? "Tuition Fee" : category === "modular_class" ? "Modular Class Fee" : "e.g. Speech Therapy Session"} className="rounded-2xl h-11 mt-1.5" data-testid="sa-desc" />
            </div>
          )}
          {!(packageMode && category === "individual_session") && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="font-heading">Date <span className="text-[#C62828]">*</span></Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-2xl h-11 mt-1.5" data-testid="sa-date" />
              </div>
              {isSimpleAmount && (
                <div>
                  <Label className="font-heading">Amount ({currency}) <span className="text-[#C62828]">*</span></Label>
                  <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-2xl h-11 mt-1.5 tabular-nums" data-testid="sa-amount-inline" />
                </div>
              )}
            </div>
          )}

          {needsInventory && (
            <div>
              <Label className="font-heading">{category === "uniforms" ? "Uniform Item" : category === "books" ? "Book / Material" : "Inventory Item"}</Label>
              <select value={inventoryItemId} onChange={(e) => setInventoryItemId(e.target.value)} className="mt-1.5 w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm" data-testid="sa-inv-select">
                <option value="">Select inventory item</option>
                {invForCategory.map((i) => <option key={i.id} value={i.id}>{i.name} — stock: {i.closing_stocks ?? 0} · {fmt(i.unit_price)}</option>)}
              </select>
            </div>
          )}

          {needsEvent && (
            <div>
              <Label className="font-heading">Event Name</Label>
              <select value={eventId} onChange={(e) => setEventId(e.target.value)} className="mt-1.5 w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm" data-testid="sa-event-select">
                <option value="">Select event</option>
                {events.filter((e) => e.status === "active").map((e) => <option key={e.id} value={e.id}>{e.name} — {fmt(e.fee_amount)}</option>)}
              </select>
            </div>
          )}

          {!isSimpleAmount && (
            <div>
              <Label className="font-heading">Description <span className="text-[#C62828]">*</span></Label>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={category === "tuition" ? "Tuition Fee" : "e.g. T-SHIRT (S10)"} className="rounded-2xl h-11 mt-1.5" data-testid="sa-desc" />
            </div>
          )}

          {category === "uniforms" && (
            <div>
              <Label className="font-heading">Size</Label>
              <Input value={size} onChange={(e) => setSize(e.target.value)} className="rounded-2xl h-11 mt-1.5" placeholder="e.g. S10" data-testid="sa-size" />
            </div>
          )}

          {!isSimpleAmount && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="font-heading">Quantity <span className="text-[#C62828]">*</span></Label>
                <div className="mt-1.5 flex items-stretch rounded-2xl border border-input bg-white overflow-hidden h-11 w-full max-w-[180px]">
                  <button type="button" onClick={() => setQuantity(String(Math.max(1, Number(quantity || 1) - 1)))} className="w-10 hover:bg-brand-purpleLight flex items-center justify-center" data-testid="sa-qty-minus"><Minus className="w-4 h-4" /></button>
                  <Input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="h-full flex-1 text-center border-0 rounded-none tabular-nums focus-visible:ring-0" data-testid="sa-qty" />
                  <button type="button" onClick={() => setQuantity(String(Math.max(1, Number(quantity || 0) + 1)))} className="w-10 hover:bg-brand-purpleLight flex items-center justify-center" data-testid="sa-qty-plus"><Plus className="w-4 h-4" /></button>
                </div>
              </div>
              <div>
                <Label className="font-heading">Unit Price ({currency})</Label>
                <Input type="number" min="0" step="0.01" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} className="rounded-2xl h-11 mt-1.5 tabular-nums" data-testid="sa-unit-price" />
              </div>
            </div>
          )}
          {!isSimpleAmount && (
            <div>
              <Label className="font-heading">Total Amount ({currency}) <span className="text-[#C62828]">*</span></Label>
              <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-2xl h-11 mt-1.5 tabular-nums" data-testid="sa-amount" />
              <p className="text-[11px] text-brand-inkMuted mt-1">Computed: {fmt(computedAmount)}</p>
            </div>
          )}
          <div>
            <Label className="font-heading">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-2xl mt-1.5 min-h-[80px]" data-testid="sa-notes" />
          </div>

          {/* Optional initial payment — fill these in to record an upfront payment along with the record. */}
          <div className="rounded-2xl border border-brand-purple/10 bg-[#F6F8FE]/50 p-4 space-y-3">
            <div>
              <p className="font-heading font-semibold text-sm text-foreground">Initial Payment <span className="text-brand-inkMuted font-normal">(optional)</span></p>
              <p className="text-[11px] text-brand-inkMuted mt-0.5">Leave Paid Now at 0 if no payment has been received yet.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="font-heading">Paid Now ({currency})</Label>
                <Input type="number" min="0" step="0.01" value={paidNow} onChange={(e) => setPaidNow(e.target.value)} className="rounded-2xl h-11 mt-1.5 tabular-nums bg-white" data-testid="sa-paid-now" />
              </div>
              <div>
                <Label className="font-heading">Mode of Payment</Label>
                <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="mt-1.5 w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm" data-testid="sa-payment-method">
                  {PAYMENT_MODES.map((m) => (<option key={m.value} value={m.value}>{m.label}</option>))}
                </select>
              </div>
            </div>
            <div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="font-heading">Reference No.</Label>
                  <Input value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} className="rounded-2xl h-11 mt-1.5 bg-white" placeholder="e.g. GCash txn / cheque #" data-testid="sa-reference-no" />
                </div>
                <div>
                  <Label className="font-heading">Payment Date</Label>
                  <Input
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    max={todayISO()}
                    className="rounded-2xl h-11 mt-1.5 bg-white"
                    data-testid="sa-payment-date"
                  />
                  <p className="text-[10.5px] text-brand-inkMuted mt-1">Shown on the Income → Payment Received report.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Release — only for goods/events. Optional at record-creation time; the
              admin can also add more release entries later from the record view. */}
          {(needsInventory || needsEvent) && (
            <div className="rounded-2xl border border-brand-purple/10 bg-[#F0FDF4]/60 p-4 space-y-3" data-testid="sa-release-section">
              <div>
                <p className="font-heading font-semibold text-sm text-foreground">Release <span className="text-brand-inkMuted font-normal">(optional)</span></p>
                <p className="text-[11px] text-brand-inkMuted mt-0.5">
                  Log how many items are being handed over now. Must be fully paid to release, and deducts from inventory when linked. Partial releases can be added later.
                </p>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label className="font-heading">Qtty of Items Released</Label>
                  <Input
                    type="number" min="0" step="1"
                    max={quantity || undefined}
                    value={releaseQty}
                    onChange={(e) => setReleaseQty(e.target.value)}
                    placeholder="0"
                    className="rounded-2xl h-11 mt-1.5 tabular-nums bg-white"
                    data-testid="sa-release-qty"
                  />
                  <p className="text-[10.5px] text-brand-inkMuted mt-1">Max: {quantity || 0}</p>
                </div>
                <div>
                  <Label className="font-heading">Date of Release</Label>
                  <Input
                    type="date"
                    value={releaseDate}
                    onChange={(e) => setReleaseDate(e.target.value)}
                    max={todayISO()}
                    className="rounded-2xl h-11 mt-1.5 bg-white"
                    data-testid="sa-release-date"
                  />
                </div>
                <div>
                  <Label className="font-heading">Released By</Label>
                  <Input
                    value={currentUserName || "—"}
                    disabled
                    className="rounded-2xl h-11 mt-1.5 bg-white opacity-70"
                    data-testid="sa-released-by"
                  />
                  <p className="text-[10.5px] text-brand-inkMuted mt-1">Auto-filled from logged-in user.</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-2xl h-11" data-testid="sa-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-11 px-6" data-testid="sa-submit">{saving ? "Saving..." : "Add Record"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------- Pay Dialog ------------------------------ */
/* ------------------------------ Release Dialog ---------------------------- */
function ReleaseDialog({ record, currentUserName, onClose, onSaved }) {
  const alreadyReleased = (record.releases || []).reduce((s, x) => s + (Number(x.quantity) || 0), 0);
  const remaining = Math.max(0, Number(record.quantity || 0) - alreadyReleased);
  const [qty, setQty] = useState(String(remaining));
  const [date, setDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const q = Math.max(0, Number(qty || 0));
    if (q < 1) { toast.error("Quantity must be at least 1"); return; }
    if (q > remaining) { toast.error(`Only ${remaining} remaining`); return; }
    setSaving(true);
    try {
      await api.post(`/student-accounts/${record.id}/release`, { quantity: q, date });
      toast.success("Release recorded");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md rounded-3xl" data-testid="sa-release-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Release Items</DialogTitle>
          <DialogDescription className="text-brand-inkMuted uppercase">
            {record.student_name} — {record.description || record.inventory_item_name || record.event_name || record.category}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-2xl border border-brand-purple/10 bg-[#F0FDF4]/50 p-3 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-brand-inkMuted">Already released</span>
              <span className="font-heading text-foreground">{alreadyReleased} of {record.quantity || 0}</span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-brand-inkMuted">Remaining</span>
              <span className="font-heading text-[#166534]">{remaining}</span>
            </div>
          </div>
          <div>
            <Label className="font-heading">Qtty of Items Released <span className="text-[#C62828]">*</span></Label>
            <Input
              type="number" min="1" max={remaining} step="1"
              value={qty} onChange={(e) => setQty(e.target.value)}
              className="rounded-2xl h-11 mt-1.5 tabular-nums"
              data-testid="sa-release-dialog-qty"
              autoFocus
            />
            <p className="text-[10.5px] text-brand-inkMuted mt-1">Max: {remaining}</p>
          </div>
          <div>
            <Label className="font-heading">Date of Release <span className="text-[#C62828]">*</span></Label>
            <Input
              type="date" value={date} onChange={(e) => setDate(e.target.value)} max={todayISO()}
              className="rounded-2xl h-11 mt-1.5"
              data-testid="sa-release-dialog-date"
            />
          </div>
          <div>
            <Label className="font-heading">Released By</Label>
            <Input
              value={currentUserName || "—"} disabled
              className="rounded-2xl h-11 mt-1.5 opacity-70"
              data-testid="sa-release-dialog-by"
            />
            <p className="text-[10.5px] text-brand-inkMuted mt-1">Auto-filled from logged-in user.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-2xl h-11">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-11 px-6" data-testid="sa-release-dialog-submit">
              {saving ? "Saving…" : "Log Release"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


/* ------------------------------ Pay Dialog -------------------------------- */
function PayDialog({ record, currency, onClose, onSaved }) {
  const remaining = Math.max(0, Number(record.amount || 0) - Number(record.paid_amount || 0));
  const [amount, setAmount] = useState(String(remaining));
  const [mode, setMode] = useState("cash");
  const [referenceNo, setReferenceNo] = useState("");
  const [paymentDate, setPaymentDate] = useState(todayISO());
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const fmt = (v) => `${currency}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount || 0);
    if (amt <= 0) { toast.error("Amount must be positive"); return; }
    if (amt > remaining + 0.001) { toast.error(`Cannot exceed remaining ${fmt(remaining)}`); return; }
    setSaving(true);
    try {
      await api.post(`/student-accounts/${record.id}/pay`, {
        amount: amt, mode, reference_no: referenceNo.trim(), note,
        payment_date: paymentDate,
      });
      toast.success("Payment recorded");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md rounded-3xl" data-testid="sa-pay-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Record Payment</DialogTitle>
          <DialogDescription className="text-brand-inkMuted">{record.student_name} · {record.description || "Record"}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-2xl bg-[#F6F8FE] p-3">
              <p className="text-[10px] text-brand-inkMuted">AMOUNT</p>
              <p className="font-heading font-semibold mt-1 tabular-nums">{fmt(record.amount)}</p>
            </div>
            <div className="rounded-2xl bg-[#F6F8FE] p-3">
              <p className="text-[10px] text-brand-inkMuted">PAID</p>
              <p className="font-heading font-semibold mt-1 tabular-nums">{fmt(record.paid_amount)}</p>
            </div>
            <div className="rounded-2xl bg-brand-purpleLight/40 p-3">
              <p className="text-[10px] text-brand-inkMuted">BALANCE</p>
              <p className="font-heading font-semibold mt-1 tabular-nums text-brand-purple">{fmt(remaining)}</p>
            </div>
          </div>
          <div>
            <Label className="font-heading">Payment Amount <span className="text-[#C62828]">*</span></Label>
            <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-2xl h-11 mt-1.5 tabular-nums" data-testid="sa-pay-amount" autoFocus />
          </div>
          <div>
            <Label className="font-heading">Mode of Payment</Label>
            <select value={mode} onChange={(e) => setMode(e.target.value)} className="mt-1.5 w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm" data-testid="sa-pay-mode">
              {PAYMENT_MODES.map((m) => (<option key={m.value} value={m.value}>{m.label}</option>))}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="font-heading">Reference No.</Label>
              <Input value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} className="rounded-2xl h-11 mt-1.5" placeholder="e.g. GCash txn / cheque #" data-testid="sa-pay-reference" />
            </div>
            <div>
              <Label className="font-heading">Payment Date</Label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                max={todayISO()}
                className="rounded-2xl h-11 mt-1.5"
                data-testid="sa-pay-date"
              />
            </div>
          </div>
          <div>
            <Label className="font-heading">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="rounded-2xl h-11 mt-1.5" data-testid="sa-pay-note" />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-2xl h-11">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-11 px-6" data-testid="sa-pay-submit">{saving ? "Saving..." : "Record Payment"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------- View Dialog ------------------------------ */
function ViewDialog({ record: initialRecord, currency, onClose, onChanged }) {
  const [record, setRecord] = useState(initialRecord);
  const [editingPayment, setEditingPayment] = useState(null);
  useEffect(() => { setRecord(initialRecord); }, [initialRecord]);
  const fmt = (v) => `${currency}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg rounded-3xl" data-testid="sa-view-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{record.student_name}</DialogTitle>
          <DialogDescription className="text-brand-inkMuted">{record.description || record.inventory_item_name || record.event_name || record.category}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Info label="Date">{record.date || "—"}</Info>
            <Info label="Category">{record.category}</Info>
            {/* Quantity / Unit Price are inventory-related fields. Hide them
                on Tuition and Individual-Session records where every row is
                a single billable item — showing "Qty 1 · Unit ₱0.00" only
                added noise. Other categories (uniforms, books, events,
                others) still see these fields. */}
            {record.category !== "tuition" && record.category !== "modular_class" && record.category !== "individual_session" && (
              <>
                <Info label="Quantity">{record.quantity}</Info>
                <Info label="Unit Price">{fmt(record.unit_price)}</Info>
              </>
            )}
            <Info label="Amount">{fmt(record.amount)}</Info>
            <Info label="Paid">{fmt(record.paid_amount)}</Info>
            <Info label="Balance" accent>{fmt(record.balance)}</Info>
            <Info label="Status"><StatusPill status={record.status} /></Info>
          </div>
          {record.category === "individual_session" && (
            <div className="rounded-2xl border border-slate-200 bg-[#FAFBFE] p-3">
              <p className="text-[11px] uppercase tracking-wider text-brand-inkMuted font-heading mb-2">Session attendance</p>
              <div className="flex items-center gap-2 flex-wrap">
                <AttendancePill record={record} />
                {record.attended && record.attended_date && (
                  <span className="text-xs text-brand-inkMuted">on <span className="font-heading text-foreground">{fmtRowDate(record.attended_date)}</span></span>
                )}
                {record.attended && record.attended_marked_by_name && (
                  <span className="text-xs text-brand-inkMuted">· marked by <span className="text-foreground font-medium">{record.attended_marked_by_name}</span></span>
                )}
              </div>
              {record.attended && record.attended_note && (
                <p className="text-xs text-brand-inkMuted mt-2">Note: <span className="text-foreground">{record.attended_note}</span></p>
              )}
            </div>
          )}
          {record.notes && <Info label="Notes">{record.notes}</Info>}
          {(record.payments || []).length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-brand-inkMuted font-heading mb-1.5">Payments</p>
              <ul className="space-y-1.5">
                {record.payments.map((p) => (
                  <li key={p.id} className="flex items-start justify-between gap-3 px-3 py-2 rounded-xl bg-[#F6F8FE]" data-testid={`sa-payment-row-${p.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm">
                        <span className="font-heading">{fmt(p.amount)}</span>
                        <span className="text-brand-inkMuted text-xs ml-1.5">· {formatMode(p.mode)}</span>
                        {p.reference_no ? <span className="text-brand-inkMuted text-xs ml-1.5 font-mono">· Ref: {p.reference_no}</span> : null}
                      </div>
                      {p.recorded_by_name ? (
                        <div className="text-[11px] text-brand-inkMuted mt-0.5">Received by: <span className="text-foreground font-medium">{p.recorded_by_name}</span></div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-brand-inkMuted whitespace-nowrap">{new Date(p.at).toLocaleString()}</span>
                      <button
                        type="button"
                        onClick={() => setEditingPayment(p)}
                        className="w-7 h-7 inline-flex items-center justify-center rounded-lg text-brand-purple hover:bg-brand-purpleLight transition"
                        data-testid={`sa-payment-edit-${p.id}`}
                        aria-label="Edit payment entry"
                        title="Edit payment"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Releases: shows per-release history + button to log another
              partial release. Fully-released items collapse the button. */}
          {["uniforms", "books", "events", "others"].includes(record.category) && (
            <ReleasesPanel record={record} onChanged={(updated) => { setRecord(updated); onChanged && onChanged(); }} />
          )}
        </div>
        <DialogFooter><Button onClick={onClose} className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-11 px-5">Close</Button></DialogFooter>
      </DialogContent>
      {editingPayment && (
        <EditPaymentDialog
          recordId={record.id}
          payment={editingPayment}
          currency={currency}
          onClose={() => setEditingPayment(null)}
          onSaved={(updated) => { setRecord(updated); setEditingPayment(null); onChanged && onChanged(); }}
        />
      )}
    </Dialog>
  );
}

/* --------------------------- Releases sub-panel --------------------------- */
function ReleasesPanel({ record, onChanged }) {
  const releases = record.releases || [];
  const totalReleased = releases.reduce((s, r) => s + (Number(r.quantity) || 0), 0);
  const remaining = Math.max(0, Number(record.quantity || 0) - totalReleased);
  const fullyPaid = Number(record.paid_amount || 0) + 0.001 >= Number(record.amount || 0);
  const [adding, setAdding] = useState(false);
  const [qty, setQty] = useState("");
  const [date, setDate] = useState(todayISO());
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const q = Math.max(0, Number(qty || 0));
    if (q < 1) { toast.error("Quantity must be at least 1"); return; }
    if (q > remaining) { toast.error(`Only ${remaining} remaining`); return; }
    if (!fullyPaid) { toast.error("Item must be fully paid before release"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/student-accounts/${record.id}/release`, { quantity: q, date });
      toast.success("Release logged");
      setAdding(false); setQty(""); setDate(todayISO());
      onChanged && onChanged(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-2xl border border-brand-purple/10 bg-[#F0FDF4]/50 p-3" data-testid="sa-releases-panel">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-brand-inkMuted font-heading">Releases</p>
          <p className="text-xs text-brand-inkMuted mt-0.5">
            {totalReleased} of {record.quantity || 0} released
            {remaining > 0 && ` — ${remaining} remaining`}
          </p>
        </div>
        {remaining > 0 && !adding && (
          <Button
            type="button" size="sm" variant="outline"
            disabled={!fullyPaid}
            onClick={() => setAdding(true)}
            className="rounded-full h-8 px-3 text-xs border-brand-purple/30 text-brand-purple hover:bg-brand-purpleLight/30"
            title={fullyPaid ? "Add release" : "Item must be fully paid first"}
            data-testid="sa-add-release-btn"
          >
            + Add Release
          </Button>
        )}
      </div>

      {!fullyPaid && remaining > 0 && !adding && (
        <p className="text-[11px] text-[#C62828] italic mb-2">Cannot release yet — outstanding balance.</p>
      )}

      {releases.length === 0 ? (
        <p className="text-xs text-brand-inkMuted italic">No releases yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {releases.map((r) => (
            <li key={r.id} className="flex items-center justify-between px-3 py-2 rounded-xl bg-white border border-brand-purple/10 text-xs" data-testid={`sa-release-row-${r.id}`}>
              <div>
                <p className="font-heading text-foreground">{r.quantity} pc{r.quantity === 1 ? "" : "s"} · {r.date}</p>
                <p className="text-brand-inkMuted mt-0.5">by {r.released_by_name || "—"}</p>
              </div>
              <span className="rounded-full bg-[#DCFCE7] text-[#166534] px-2 py-0.5 text-[10px] font-heading uppercase">Released</span>
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <div className="mt-3 grid sm:grid-cols-2 gap-2 p-3 rounded-2xl bg-white border border-brand-purple/10">
          <div>
            <Label className="font-heading text-xs">Qtty Released</Label>
            <Input
              type="number" min="1" max={remaining} step="1"
              value={qty} onChange={(e) => setQty(e.target.value)}
              placeholder={String(remaining)}
              className="rounded-2xl h-10 mt-1 tabular-nums"
              data-testid="sa-release-qty-input"
              autoFocus
            />
          </div>
          <div>
            <Label className="font-heading text-xs">Date of Release</Label>
            <Input
              type="date" value={date} onChange={(e) => setDate(e.target.value)} max={todayISO()}
              className="rounded-2xl h-10 mt-1"
              data-testid="sa-release-date-input"
            />
          </div>
          <div className="sm:col-span-2 flex justify-end gap-2 mt-1">
            <Button type="button" variant="outline" size="sm" onClick={() => { setAdding(false); setQty(""); }} className="rounded-full h-9">Cancel</Button>
            <Button type="button" size="sm" disabled={saving} onClick={submit} className="rounded-full h-9 bg-brand-purple hover:bg-brand-purpleHover text-white px-4" data-testid="sa-release-submit">{saving ? "Saving…" : "Log Release"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}


/* --------------------- Edit Payment Entry Dialog --------------------- */
function EditPaymentDialog({ recordId, payment, currency, onClose, onSaved }) {
  // Pre-fill with the existing payment values. The `at` field is an ISO
  // timestamp — slice to YYYY-MM-DD for the date input.
  const initialDate = (payment.at || "").slice(0, 10) || todayISO();
  const [amount, setAmount] = useState(String(payment.amount ?? "0"));
  const [mode, setMode] = useState(payment.mode || "cash");
  const [referenceNo, setReferenceNo] = useState(payment.reference_no || "");
  const [paymentDate, setPaymentDate] = useState(initialDate);
  const [note, setNote] = useState(payment.note || "");
  const [saving, setSaving] = useState(false);
  const fmt = (v) => `${currency}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount || 0);
    if (amt <= 0) { toast.error("Amount must be positive"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/student-accounts/${recordId}/payments/${payment.id}`, {
        amount: amt, mode, reference_no: referenceNo.trim(), note: note.trim(),
        payment_date: paymentDate,
      });
      toast.success("Payment updated");
      onSaved && onSaved(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="sm:max-w-md rounded-3xl" data-testid="sa-edit-payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Pencil className="w-4 h-4 text-brand-purple" /> Edit Payment
          </DialogTitle>
          <DialogDescription className="text-brand-inkMuted">
            Editing: {fmt(payment.amount)} · {formatMode(payment.mode)}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="font-heading">Amount</Label>
              <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                className="rounded-2xl h-11 mt-1.5 tabular-nums" data-testid="sa-edit-pay-amount" />
            </div>
            <div>
              <Label className="font-heading">Mode of Payment</Label>
              <select value={mode} onChange={(e) => setMode(e.target.value)}
                className="mt-1.5 w-full h-11 rounded-2xl border border-input bg-white px-3 text-sm" data-testid="sa-edit-pay-mode">
                {PAYMENT_MODES.map((m) => (<option key={m.value} value={m.value}>{m.label}</option>))}
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="font-heading">Reference No.</Label>
              <Input value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)}
                className="rounded-2xl h-11 mt-1.5" placeholder="e.g. GCash txn / cheque #" data-testid="sa-edit-pay-reference" />
            </div>
            <div>
              <Label className="font-heading">Payment Date</Label>
              <Input type="date" value={paymentDate} max={todayISO()}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="rounded-2xl h-11 mt-1.5" data-testid="sa-edit-pay-date" />
              <p className="text-[10.5px] text-brand-inkMuted mt-1">Shown on the Income → Payment Received report.</p>
            </div>
          </div>
          <div>
            <Label className="font-heading">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
              className="rounded-2xl h-11 mt-1.5" data-testid="sa-edit-pay-note" />
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-2xl h-11">Cancel</Button>
            <Button type="submit" disabled={saving}
              className="rounded-2xl bg-brand-purple hover:bg-brand-purpleHover text-white h-11 px-6"
              data-testid="sa-edit-pay-submit">{saving ? "Saving..." : "Save Changes"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, accent, children }) {
  return (
    <div className="rounded-xl border border-brand-purple/10 px-3 py-2">
      <p className="text-[10px] uppercase tracking-wider text-brand-inkMuted font-heading">{label}</p>
      <p className={`mt-0.5 font-heading ${accent ? "text-brand-purple font-semibold" : "text-foreground"}`}>{children}</p>
    </div>
  );
}
